import { Check, FolderPlus, Loader2, Search } from "lucide-react";
import { useState, type ReactElement } from "react";
import { Button } from "../../../../shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../../../shared/components/ui/dialog";
import { Input } from "../../../../shared/components/ui/input";
import { useCollections } from "../hooks/useCollections";

interface CollectionPickerModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSelectCollection: (collectionKey: string) => void;
  readonly selectedCount?: number;
  readonly titleCollections?: Array<{ key: string; name: string }>;
  readonly onRemoveFromCollection?: (collectionKey: string) => void;
}

interface CollectionItemProps {
  readonly collection: { key: string; name: string; visible: boolean };
  readonly isInCollection: boolean;
  readonly isSingleTitle: boolean;
  readonly onAdd: (key: string) => void;
  readonly onRemove?: (key: string) => void;
}

/**
 * Collection item button
 */
function CollectionItem({
  collection,
  isInCollection,
  isSingleTitle,
  onAdd,
  onRemove,
}: CollectionItemProps): ReactElement {
  const handleClick = () => {
    if (isInCollection && isSingleTitle && onRemove) {
      onRemove(collection.key);
    } else {
      onAdd(collection.key);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left group ${
        isInCollection
          ? "border-lumi-accent bg-lumi-accent/10"
          : "border-neutral-700 bg-neutral-900/30 hover:bg-neutral-800/50 hover:border-lumi-accent/50"
      }`}
    >
      <div className="flex-1">
        <div
          className={`font-medium transition-colors ${
            isInCollection ? "text-lumi-accent" : "text-white group-hover:text-lumi-accent"
          }`}
        >
          {collection.name}
          {isInCollection && isSingleTitle && (
            <span className="ml-2 text-xs text-lumi-accent/70">(Click to remove)</span>
          )}
        </div>
        <div className="text-xs text-neutral-500 mt-1">
          {collection.key} • {collection.visible ? "Visible" : "Hidden"}
        </div>
      </div>
      {isInCollection ? (
        <Check className="h-5 w-5 text-lumi-accent" />
      ) : (
        <FolderPlus className="h-5 w-5 text-neutral-600 group-hover:text-lumi-accent transition-colors" />
      )}
    </button>
  );
}

/**
 * Modal for selecting a collection to add titles to
 */
export function CollectionPickerModal({
  open,
  onClose,
  onSelectCollection,
  selectedCount,
  titleCollections = [],
  onRemoveFromCollection,
}: CollectionPickerModalProps): ReactElement {
  const { collections, loading, error } = useCollections();
  const [searchQuery, setSearchQuery] = useState("");

  const isSingleTitle = selectedCount === undefined || selectedCount === 1;
  const titleCollectionKeys = new Set(titleCollections.map((c) => c.key));
  const countText = (selectedCount ?? 0) > 1 ? `${selectedCount} titles` : "title";

  // Filter collections by search query
  const filteredCollections = collections.filter((collection) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return collection.name.toLowerCase().includes(query) || collection.key.toLowerCase().includes(query);
  });

  const handleSelect = (collectionKey: string) => {
    onSelectCollection(collectionKey);
    onClose();
    setSearchQuery(""); // Reset search on close
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg border-neutral-900 bg-neutral-900/30 backdrop-blur-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderPlus className="h-5 w-5 text-lumi-accent" />
            {isSingleTitle ? "Manage Collections" : "Add to Collection"}
          </DialogTitle>
          <DialogDescription>
            {isSingleTitle ? "Add or remove this title from collections" : `Select a collection to add ${countText} to`}
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input
            type="text"
            placeholder="Search collections..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Collections List */}
        <div className="max-h-96 overflow-y-auto space-y-2">
          {loading && (
            <div className="flex items-center justify-center py-8 text-neutral-400">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Loading collections...
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400 text-sm">{error}</div>
          )}

          {!loading && !error && filteredCollections.length === 0 && (
            <div className="text-center py-8 text-neutral-400 text-sm">
              {searchQuery ? "No collections found matching your search." : "No collections available."}
            </div>
          )}

          {!loading &&
            !error &&
            filteredCollections.map((collection) => {
              const isInCollection = titleCollectionKeys.has(collection.key);

              return (
                <CollectionItem
                  key={collection.key}
                  collection={collection}
                  isInCollection={isInCollection}
                  isSingleTitle={isSingleTitle}
                  onAdd={handleSelect}
                  onRemove={onRemoveFromCollection}
                />
              );
            })}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-700">
          <Button variant="ghost-sm" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
